<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  category: Object,
  cityUrl: String,
  main_usluga_url: String,
  second_usluga_url: String,
  hasQuestions: { type: Boolean, default: false },
  hasDocuments: { type: Boolean, default: false },
  hasReviews: { type: Boolean, default: false },
  hasArticles: { type: Boolean, default: false },
  hasCities: { type: Boolean, default: false },
  hasCourt: { type: Boolean, default: false },
});

// Строка поиска: пока пуста — показываем текущую ветку, иначе совпадения.
const q = ref("");

const cats = computed(() => Object.values(props.category || {}));

// Активная категория из URL
const activeMain = computed(() =>
  cats.value.find((u) => u.url == props.main_usluga_url) || null
);
const activeSecond = computed(() => {
  const second = activeMain.value?.mainhassecond;
  if (!second || !second.length) return null;
  return second.find((s) => s.url == props.second_usluga_url) || null;
});

// Подуслуги текущего направления — веткой под ним
const subcats = computed(() => activeMain.value?.mainhassecond || []);

// Длинная ветка: показываем первые SUB_LIMIT, остальное — по «Ещё N». Текущая
// подуслуга видна всегда, даже если стоит дальше лимита. Скрытые пункты остаются
// в разметке (v-show), чтобы перелинковка не терялась.
// Сворачиваем только ветки длиннее SUB_LIMIT + 2: кнопка «Ещё 1» хуже, чем сама строка.
const SUB_LIMIT = 6;
const subsOpen = ref(false);
const subsCollapsible = computed(() => subcats.value.length > SUB_LIMIT + 2);
const subsHidden = computed(() => !subsCollapsible.value ? 0 :
  subcats.value.filter((s, i) => i >= SUB_LIMIT && s.url != props.second_usluga_url).length
);
function subShown(second, i) {
  return !subsCollapsible.value || subsOpen.value || i < SUB_LIMIT || second.url == props.second_usluga_url;
}

// Остальные направления — раскрываются по кнопке под веткой
const othersOpen = ref(false);
const otherMains = computed(() => cats.value.filter((u) => u.url != props.main_usluga_url));

// Нижний регистр + ё→е: пользователи ё не набирают.
function norm(v) {
  return String(v || "").toLowerCase().replace(/ё/g, "е");
}

// Плоский список всего, по чему ищем: категории и их подкатегории.
// Строится один раз: список небольшой (десятки пунктов), фильтруем прямо в браузере.
const index = computed(() => {
  const out = [];
  if (!props.cityUrl) return out;
  for (const u of cats.value) {
    out.push({
      name: u.usl_name,
      parent: "",
      mainUrl: u.url,
      secondUrl: "",
      active: u.url == props.main_usluga_url && !props.second_usluga_url,
      hay: norm(u.usl_name),
    });
    for (const s of u.mainhassecond || []) {
      out.push({
        name: s.usl_name,
        parent: u.usl_name,
        mainUrl: u.url,
        secondUrl: s.url,
        active: u.url == props.main_usluga_url && s.url == props.second_usluga_url,
        // Ищем и по родителю: «наслед» должно находить «Оспорить завещание».
        hay: norm(s.usl_name + " " + u.usl_name),
      });
    }
  }
  return out;
});

const LIMIT = 12;

const words = computed(() => norm(q.value).trim().split(/\s+/).filter(Boolean));
const tooShort = computed(() => norm(q.value).trim().length < 2);

const matches = computed(() => {
  if (tooShort.value) return [];
  const w = words.value;
  const hits = index.value.filter((it) => w.every((x) => it.hay.includes(x)));
  // Совпадения с начала слова выше: «дтп» → «ДТП», а не «выплаты после ДТП».
  const rank = (it) => (it.hay.startsWith(w[0]) ? 0 : 1) + (it.parent ? 0.5 : 0);
  return hits.sort((a, b) => rank(a) - rank(b));
});

const found = computed(() => matches.value.slice(0, LIMIT));
const rest = computed(() => Math.max(0, matches.value.length - LIMIT));

/**
 * Разбивает название на куски с пометкой «это совпало» — чтобы человек видел,
 * почему строка попала в выдачу. Возвращаем массив, а не HTML: v-html на данных
 * из БД в поиске не нужен.
 */
function parts(name) {
  const src = String(name || "");
  const low = norm(src);
  const marks = new Array(src.length).fill(false);
  for (const w of words.value) {
    let from = 0, at;
    while ((at = low.indexOf(w, from)) !== -1) {
      for (let i = at; i < at + w.length; i++) marks[i] = true;
      from = at + w.length;
    }
  }
  const out = [];
  for (let i = 0; i < src.length; i++) {
    if (out.length && out[out.length - 1].hit === marks[i]) out[out.length - 1].t += src[i];
    else out.push({ t: src[i], hit: marks[i] });
  }
  return out;
}

/**
 * Enter — переход к первому совпадению. Жмём по готовой ссылке в DOM, а не строим
 * адрес заново: route() живёт только в шаблоне (globalProperties плагина Ziggy),
 * глобальной функции с таким именем нет ни в браузере, ни в SSR.
 */
const list = ref(null);
function goFirst() {
  list.value?.querySelector("a")?.click();
}
</script>

<template>
  <div class="lg:mt-10 w-full space-y-6">
    <!-- ПОИСК ПО УСЛУГАМ.
         Раньше здесь был свёрнутый список с кнопкой «Показать другие категории»: на
         странице услуги он показывал текущую ветку, а на /uslugi — пустую карточку с
         кнопкой, обещавшей «другие» категории, когда ни одной не показано. И в любом
         случае за кнопкой открывались 46 пунктов с прокруткой без всякого поиска. -->
    <div>
      <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 px-1">
        {{ activeMain ? 'Категория' : 'Услуги' }}
      </p>

      <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <!-- Строка поиска -->
        <div class="relative p-3">
          <svg class="w-4 h-4 absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
          </svg>
          <!-- type=text, а не search: у search Chrome рисует СВОЙ крестик поверх нашего -->
          <input
            v-model="q"
            type="text"
            inputmode="search"
            enterkeyhint="search"
            autocomplete="off"
            placeholder="Найти услугу…"
            aria-label="Поиск услуги"
            class="w-full h-11 pl-9 pr-11 text-sm rounded-lg border border-gray-300 text-gray-800 placeholder-gray-500
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            @keydown.esc="q = ''"
            @keydown.enter.prevent="goFirst"
          />
          <button v-if="q" type="button" @click="q = ''" aria-label="Очистить поиск"
            class="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 inline-flex items-center justify-center
                   rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Одна буква совпадает почти со всем — ждём вторую -->
        <p v-if="q && tooShort" class="px-4 pb-4 text-sm text-gray-500">
          Введите хотя бы две буквы.
        </p>

        <!-- Ничего не нашлось -->
        <p v-else-if="q && !found.length" class="px-4 pb-4 text-sm text-gray-500">
          По запросу «{{ q }}» ничего нет. Попробуйте короче — например «наслед» или «дтп».
        </p>

        <!-- Результаты поиска -->
        <div v-else-if="q" ref="list" class="border-t border-gray-100 max-h-[60vh] overflow-y-auto">
          <a v-for="item in found" :key="item.mainUrl + '/' + item.secondUrl"
            :href="item.secondUrl
              ? route('offer.second', [cityUrl, item.mainUrl, item.secondUrl])
              : route('offer.main', [cityUrl, item.mainUrl])"
            class="block px-4 py-2.5 border-b border-gray-50 last:border-0 transition-colors hover:bg-indigo-50"
            :class="item.active ? 'bg-indigo-50' : ''">
            <span class="block text-sm leading-snug"
              :class="item.active ? 'text-indigo-700 font-semibold' : 'text-gray-800'">
              <template v-for="(p, i) in parts(item.name)" :key="i"><mark v-if="p.hit"
                class="bg-transparent text-indigo-700 font-semibold">{{ p.t }}</mark><template v-else>{{ p.t }}</template></template>
            </span>
            <span v-if="item.parent" class="block text-xs text-gray-500 mt-0.5">{{ item.parent }}</span>
          </a>
          <p v-if="rest > 0" class="px-4 py-2 text-xs text-gray-500">
            и ещё {{ rest }} — уточните запрос
          </p>
        </div>

        <!-- Пустой ввод: где мы сейчас и куда перейти -->
        <template v-else>
          <!--
            ВЕТКА ДЕРЕВОМ, А НЕ ЧИПАМИ. Было: стрелка «‹ Юрист по трудовым спорам»
            (читалась как «назад», а не как «ты в этом разделе») и подуслуги
            кнопками разной ширины, где выбранная залита целиком и похожа на
            нажатую кнопку. Теперь: направление заголовком, подуслуги лесенкой под
            ним, текущая — мягкой подложкой с полоской слева.
          -->
          <nav v-if="activeMain && cityUrl" class="px-3 pb-3" aria-label="Услуги направления">
            <a :href="route('offer.main', [cityUrl, activeMain.url])"
              class="flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
              :class="!activeSecond ? 'bg-indigo-50 text-indigo-700' : 'text-gray-900 hover:bg-gray-50 hover:text-indigo-700'"
              :aria-current="!activeSecond ? 'page' : null">
              <span class="leading-snug">{{ activeMain.usl_name }}</span>
              <span v-if="subcats.length" class="shrink-0 text-xs font-normal text-gray-400">{{ subcats.length }}</span>
            </a>

            <ul v-if="subcats.length" class="mt-1 ml-3 border-l border-gray-200">
              <li v-for="(second, i) in subcats" :key="i" v-show="subShown(second, i)">
                <a :href="route('offer.second', [cityUrl, activeMain.url, second.url])"
                  class="-ml-px block border-l-2 py-2 pl-4 pr-3 text-sm leading-snug transition-colors"
                  :class="second.url == second_usluga_url
                    ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-semibold rounded-r-lg'
                    : 'border-transparent text-gray-600 hover:text-indigo-700 hover:border-gray-300'"
                  :aria-current="second.url == second_usluga_url ? 'page' : null">
                  {{ second.usl_name }}
                </a>
              </li>
              <li v-if="subsCollapsible">
                <button type="button" @click="subsOpen = !subsOpen" :aria-expanded="subsOpen"
                  class="-ml-px w-full flex items-center gap-1 border-l-2 border-transparent py-2 pl-4 pr-3 text-sm text-indigo-700 hover:text-indigo-900
                         focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 rounded-r-lg">
                  {{ subsOpen ? 'Свернуть' : 'Ещё ' + subsHidden }}
                  <svg class="w-3.5 h-3.5 transition-transform" :class="subsOpen ? 'rotate-180' : ''"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </li>
            </ul>
          </nav>

          <!-- Без активной категории показываем сами услуги, а не пустоту с кнопкой.
               НО ТОЛЬКО НА ШИРОКОМ ЭКРАНЕ. На телефоне колонка разворачивается в
               ленту и оказывается впереди всего: замер показал, что заголовок
               страницы начинался на 704 px, а первое предложение юриста — на 1013,
               то есть после целого экрана прокрутки по списку из 46 ссылок.
               Человек пришёл по «юрист + город» и первым делом видел каталог.
               Поиск остаётся — он занимает одну строку и решает ту же задачу
               быстрее, а полный перечень на телефоне есть внизу страницы, в блоке
               «Связанные специализации и услуги». -->
          <div v-if="!activeMain" class="hidden lg:block border-t border-gray-100 max-h-[50vh] overflow-y-auto">
            <a v-for="u in cats" :key="u.url" :href="cityUrl ? route('offer.main', [cityUrl, u.url]) : '#'"
              class="block px-4 py-2.5 border-b border-gray-50 last:border-0 text-sm text-gray-800 transition-colors hover:bg-indigo-50 hover:text-indigo-700">
              {{ u.usl_name }}
            </a>
          </div>

          <!-- Остальные направления — по кнопке: без поиска тоже можно дойти до своей темы.
               Ссылки в разметке есть всегда (v-show), поисковик их видит. -->
          <div v-if="activeMain" class="border-t border-gray-100">
            <button type="button" @click="othersOpen = !othersOpen" :aria-expanded="othersOpen"
              class="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-600 hover:text-indigo-700 hover:bg-gray-50 transition-colors
                     focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600">
              <span>Другие направления <span class="text-gray-400">{{ otherMains.length }}</span></span>
              <svg class="w-4 h-4 text-gray-400 transition-transform" :class="othersOpen ? 'rotate-180' : ''"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div v-show="othersOpen" class="max-h-[50vh] overflow-y-auto border-t border-gray-100">
              <a v-for="u in otherMains" :key="u.url" :href="cityUrl ? route('offer.main', [cityUrl, u.url]) : '#'"
                class="block px-4 py-2 border-b border-gray-50 last:border-0 text-sm text-gray-700 transition-colors hover:bg-indigo-50 hover:text-indigo-700">
                {{ u.usl_name }}
              </a>
            </div>
          </div>
          <p v-else class="px-4 py-2.5 text-xs text-gray-500 border-t border-gray-100">
            <span class="hidden lg:inline">{{ index.length }} услуг — начните вводить название</span>
            <span class="lg:hidden">{{ index.length }} услуг — начните вводить название или смотрите перечень внизу страницы</span>
          </p>
        </template>
      </div>
    </div>

    <!-- На странице: быстрые ссылки на блоки (якоря). На мобильном вместо неё — sticky-меню сверху -->
    <nav class="hidden lg:block">
      <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 px-1">На странице</p>
      <div class="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 space-y-0.5">
        <a href="#offers"
          class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors text-gray-600 hover:bg-indigo-50 hover:text-indigo-700">
          <span>Специалисты</span>
          <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
        <!-- На странице с судебной практикой блок этапов заменён разбором дел,
             поэтому и пункт навигации ведёт туда же. -->
        <a :href="hasCourt ? '#court-practice' : '#steps'"
          class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors text-gray-600 hover:bg-indigo-50 hover:text-indigo-700">
          <span>{{ hasCourt ? 'Что решают суды' : 'Как мы работаем' }}</span>
          <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
        <a v-if="hasReviews" href="#reviews"
          class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors text-gray-600 hover:bg-indigo-50 hover:text-indigo-700">
          <span>Отзывы клиентов</span>
          <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
        <a v-if="hasDocuments" href="#documents"
          class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors text-gray-600 hover:bg-indigo-50 hover:text-indigo-700">
          <span>Документы</span>
          <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
        <a href="#consultation"
          class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors text-gray-600 hover:bg-indigo-50 hover:text-indigo-700">
          <span>Бесплатная консультация</span>
          <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
        <a href="#description"
          class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors text-gray-600 hover:bg-indigo-50 hover:text-indigo-700">
          <span>Описание</span>
          <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
        <a v-if="hasQuestions" href="#questions"
          class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors text-gray-600 hover:bg-indigo-50 hover:text-indigo-700">
          <span>Частые вопросы</span>
          <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
        <a v-if="hasArticles" href="#articles"
          class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors text-gray-600 hover:bg-indigo-50 hover:text-indigo-700">
          <span>Статьи по теме</span>
          <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
        <a v-if="hasCities" href="#cities"
          class="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors text-gray-600 hover:bg-indigo-50 hover:text-indigo-700">
          <span>Города</span>
          <svg class="w-3.5 h-3.5 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </nav>
  </div>
</template>
